/**
 * Edycja rekordu przez kilka osób naraz, bez komunikatu „ktoś był szybszy”.
 *
 * Formularz trzyma trzy rzeczy: to, co wpisuje człowiek (`dane`), stan, z którym
 * ostatnio się zgodził z serwerem (`baza`), i wersję rekordu, na której pracuje.
 * Co kilka sekund pyta serwer o bieżący rekord. Gdy ktoś zapisze zmiany:
 *
 *   - pola, których nie ruszałem, uaktualniają się same (z komunikatem, kto i co),
 *   - pola, które zmieniliśmy obaj, ale inaczej, zostają moje i czekają na wybór
 *     człowieka — zapis jest wtedy zablokowany, żeby nic nie zginęło po cichu.
 *
 * Gdyby zapis i tak trafił w kolizję (ktoś zapisał w ciągu ostatnich sekund),
 * serwer odpowiada KONFLIKT_WERSJI; hook pobiera rekord, scala te same zasady
 * i ponawia zapis sam — człowiek widzi to dopiero przy prawdziwym konflikcie.
 *
 * Do tego tętno „kto ma otwarty ten rekord” (POST /api/edycje/...).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { scal, type Formularz, type Konflikt, type OpcjeScalania } from './scalanie';

/** Nazwa pozycji z listy wyboru po jej identyfikatorze — do czytelnych komunikatów o konflikcie. */
export function nazwaZListy<T extends { id: number }>(
  lista: T[], id: unknown, opis: (t: T) => string,
): string | undefined {
  if (id === '' || id === null || id === undefined) return '(brak)';
  const t = lista.find((x) => String(x.id) === String(id));
  return t ? opis(t) : undefined;
}

const CO_ILE_MS = 10_000;
const MAKS_PROB = 3;

interface Tetno {
  inni: { uzytkownikId: number; imie: string; odSekund: number }[];
  ostatniaZmiana: { kto: string; ktoId: number | null; kiedy: string } | null;
}

export interface Opcje<R extends { id: number; version: number }, F extends Formularz>
  extends OpcjeScalania<F> {
  /** Klucz rodzaju rekordu w `/api/edycje/:encja` (np. „kartoteka”). */
  encja: string;
  /** Rekord, z którego otwarto formularz; `null` przy zakładaniu nowego. */
  rekord: R | null;
  pusty: F;
  doFormularza: (rekord: R) => F;
  /** Pobranie bieżącego rekordu z serwera. */
  wczytaj: (id: number) => Promise<R>;
}

export interface WspolnaEdycja<R, F extends Formularz> {
  dane: F;
  ustaw: (nowe: F | ((poprzednie: F) => F)) => void;
  wersja: number | undefined;
  /** Pozostałe osoby, które mają ten rekord otwarty. */
  inni: Tetno['inni'];
  /** Nierozstrzygnięte konflikty — dopóki są, zapis jest zablokowany. */
  konflikty: Konflikt<F>[];
  rozstrzygnij: (id: string, wybor: 'moje' | 'ich') => void;
  /** Zmiany innych osób dołączone do formularza od ostatniego zamknięcia komunikatu. */
  dolaczone: { kto: string | null; pola: string[] } | null;
  zamknijDolaczone: () => void;
  /** Komunikat po zapisie, który wymagał scalenia (formularz zostaje otwarty). */
  poScaleniu: string | null;
  /** Opakowanie zapisu: scala i ponawia przy kolizji wersji. */
  zapisz: <T>(wyslij: (dane: F, wersja: number | undefined) => Promise<T>) => Promise<T>;
  /** Wołać w `onSuccess` z zapisanym rekordem; zamyka formularz, chyba że doszło do scalenia. */
  poZapisie: (zapisany: R, zakoncz: () => void) => void;
}

export function useEdycjaWspolna<R extends { id: number; version: number }, F extends Formularz>(
  o: Opcje<R, F>,
): WspolnaEdycja<R, F> {
  const { uzytkownik } = useAuth();
  const opcje = useRef(o);
  opcje.current = o;

  const poczatkowe = useMemo(
    () => (o.rekord ? o.doFormularza(o.rekord) : o.pusty),
    // Stan początkowy wyznacza rekord, z którego otwarto formularz — nie jego późniejsze odświeżenia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [dane, ustawStan] = useState<F>(poczatkowe);
  const daneRef = useRef(dane);
  const bazaRef = useRef<F>(poczatkowe);
  const ichRef = useRef<F>(poczatkowe);
  const wersjaRef = useRef<number | undefined>(o.rekord?.version);
  const [wersja, ustawWersje] = useState(wersjaRef.current);

  /** Konflikty surowe (z ostatniego scalenia) i wybory „zostaw moje” razem z cudzą wartością, której dotyczyły. */
  const [surowe, ustawSurowe] = useState<Konflikt<F>[]>([]);
  const [zostawione, ustawZostawione] = useState<Map<string, string>>(new Map());
  const [dolaczone, ustawDolaczone] = useState<WspolnaEdycja<R, F>['dolaczone']>(null);
  const [poScaleniu, ustawPoScaleniu] = useState<string | null>(null);
  const scalonoWZapisie = useRef(false);

  const id = o.rekord?.id;

  const ustaw = useCallback((nowe: F | ((poprzednie: F) => F)) => {
    const wartosc = typeof nowe === 'function' ? (nowe as (p: F) => F)(daneRef.current) : nowe;
    daneRef.current = wartosc;
    ustawStan(wartosc);
  }, []);

  // --- tętno: kto jeszcze edytuje, kto ostatnio zapisał -------------------
  const tetno = useQuery({
    queryKey: ['edycje', o.encja, id],
    queryFn: () => api.post<Tetno>(`/edycje/${o.encja}/${id}`),
    enabled: id !== undefined,
    refetchInterval: CO_ILE_MS,
    retry: false,
  });

  useEffect(() => {
    if (id === undefined) return undefined;
    const encja = o.encja;
    return () => {
      // Zamknięcie formularza zdejmuje wpis od razu; jeśli się nie uda, wygaśnie samo.
      void fetch(`/api/edycje/${encja}/${id}`, { method: 'DELETE', credentials: 'include', keepalive: true })
        .catch(() => undefined);
    };
  }, [id, o.encja]);

  // --- scalenie z rekordem z serwera ----------------------------------------
  const synchronizuj = useCallback((rekord: R) => {
    const op = opcje.current;
    const ich = op.doFormularza(rekord);
    const s = scal(bazaRef.current, daneRef.current, ich, op);

    // Baza przesuwa się na stan serwera, ale NIE dla pól w konflikcie: tam zostaje stara,
    // żeby konflikt liczył się od nowa względem najnowszej wartości drugiej osoby.
    const polaKonfliktu = new Set(s.konflikty.map((k) => k.id.split(':')[0]!));
    const nowaBaza: Formularz = { ...ich };
    for (const pole of polaKonfliktu) nowaBaza[pole] = (bazaRef.current as Formularz)[pole];
    bazaRef.current = nowaBaza as F;
    ichRef.current = ich;
    wersjaRef.current = rekord.version;
    ustawWersje(rekord.version);
    ustaw(s.wynik);
    ustawSurowe(s.konflikty);

    if (s.cudze.length > 0) {
      ustawDolaczone((poprzednie) => ({
        kto: poprzednie?.kto ?? null,
        pola: [...new Set([...(poprzednie?.pola ?? []), ...s.cudze])],
      }));
      // „Kto” ustalamy po chwili — z dziennika, nie z samego rekordu.
      void tetno.refetch().then((t) => {
        const ostatnia = t.data?.ostatniaZmiana;
        const kto = ostatnia && ostatnia.ktoId !== uzytkownik?.id ? ostatnia.kto : null;
        ustawDolaczone((d) => (d ? { ...d, kto } : d));
      });
    }
    return s;
  }, [tetno, ustaw, uzytkownik?.id]);

  // --- odświeżanie rekordu na żywo -----------------------------------------
  const biezacy = useQuery({
    queryKey: ['wspolna', o.encja, id],
    queryFn: () => opcje.current.wczytaj(id!),
    enabled: id !== undefined,
    refetchInterval: CO_ILE_MS,
    retry: false,
  });

  useEffect(() => {
    const r = biezacy.data;
    if (!r || wersjaRef.current === undefined || r.version <= wersjaRef.current) return;
    synchronizuj(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biezacy.data]);

  // --- konflikty do pokazania: surowe bez tych, które człowiek świadomie zostawił -----
  const konflikty = useMemo(
    () => surowe.filter((k) => zostawione.get(k.id) !== k.opisIch),
    [surowe, zostawione],
  );

  const rozstrzygnij = useCallback((konfliktId: string, wybor: 'moje' | 'ich') => {
    const k = surowe.find((x) => x.id === konfliktId);
    if (!k) return;
    if (wybor === 'ich') {
      ustaw((d) => k.przejmijIch(d));
      ustawSurowe((lista) => lista.filter((x) => x.id !== konfliktId));
    } else {
      ustawZostawione((m) => new Map(m).set(konfliktId, k.opisIch));
    }
  }, [surowe, ustaw]);

  const zapisz = useCallback(async <T,>(
    wyslij: (d: F, wersja: number | undefined) => Promise<T>,
  ): Promise<T> => {
    if (konflikty.length > 0) {
      throw new BladApi(
        'KONFLIKT_WERSJI',
        'Ktoś zmienił te same pola co Ty. Wybierz w formularzu, której wartości użyć — dopiero potem zapisz.',
        409,
      );
    }
    const op = opcje.current;
    for (let proba = 0; ; proba += 1) {
      try {
        return await wyslij(daneRef.current, wersjaRef.current);
      } catch (e) {
        const kolizja = e instanceof BladApi && e.kod === 'KONFLIKT_WERSJI';
        if (!kolizja || !op.rekord || proba >= MAKS_PROB) throw e;

        // Ktoś zdążył zapisać między odświeżeniami — scalamy i ponawiamy bez udziału człowieka.
        const aktualny = await op.wczytaj(op.rekord.id);
        scalonoWZapisie.current = true;
        const s = synchronizuj(aktualny);
        if (s.konflikty.length > 0) {
          throw new BladApi(
            'KONFLIKT_WERSJI',
            'Ktoś zapisał zmiany w tym samym czasie i zmienił też pola, które edytujesz. ' +
            'Wybierz w formularzu, której wartości użyć, i zapisz ponownie.',
            409,
          );
        }
      }
    }
  }, [konflikty.length, synchronizuj]);

  const poZapisie = useCallback((zapisany: R, zakoncz: () => void) => {
    if (!scalonoWZapisie.current) { zakoncz(); return; }
    scalonoWZapisie.current = false;

    // Formularz zostaje otwarty, żeby człowiek zobaczył, co do jego zmian dołączono.
    const f = opcje.current.doFormularza(zapisany);
    bazaRef.current = f;
    ichRef.current = f;
    wersjaRef.current = zapisany.version;
    ustawWersje(zapisany.version);
    ustaw(f);
    ustawSurowe([]);
    ustawZostawione(new Map());
    ustawPoScaleniu('Zapisano. Do Twoich zmian dołączono zmiany wprowadzone w tym samym czasie przez inną osobę.');
  }, [ustaw]);

  return {
    dane, ustaw, wersja,
    inni: tetno.data?.inni ?? [],
    konflikty, rozstrzygnij,
    dolaczone, zamknijDolaczone: () => ustawDolaczone(null),
    poScaleniu,
    zapisz, poZapisie,
  };
}
